/**
 * ScorecardMetricAggregationValidator - Alpine.js Data Component
 * Validates metric data aggregations across different time periods
 * 
 * Validates that aggregated data (weekly, monthly, quarterly, yearly) matches
 * the calculated values from source data (daily -> weekly/monthly, monthly -> quarterly/yearly)
 * 
 * All functionality is namespaced under 'metricValidator' to avoid key collisions
 */
import { workspaceToday } from '../workspace-today';

export default () => ({
    metricValidator: {
        TOLERANCE_PERCENT: 0.06, // 6% tolerance for validation
        
        consolidatedReport: {
            failures: [],
            totalMetrics: 0,
            metricsWithFailures: 0,
            totalFailures: 0
        },
        validationInProgress: false,
        // The workspace's timezone, set by validateAllMetrics(): the running periods it skips are the server's.
        timezone: null,
        modalOpen: false,
        activeMetricId: null,

        isDragging: false,
        dragStartX: 0,
        dragStartY: 0,
        modalX: 0,
        modalY: 0,
        
        startDrag(event) {
            this.isDragging = true;
            this.dragStartX = event.clientX - this.modalX;
            this.dragStartY = event.clientY - this.modalY;
            document.body.style.userSelect = 'none';
        },
        
        onDrag(event) {
            if (this.isDragging) {
                this.modalX = event.clientX - this.dragStartX;
                this.modalY = event.clientY - this.dragStartY;
            }
        },
        
        stopDrag() {
            this.isDragging = false;
            document.body.style.userSelect = '';
        },
        
        ensureHeaderVisible() {
            const modalElement = document.querySelector('[data-validation-modal]');
            if (!modalElement) return;
            
            const modalRect = modalElement.getBoundingClientRect();
            const headerHeight = 100;

            if (modalRect.top < 0) {
                this.modalY = this.modalY + Math.abs(modalRect.top) + 20;
            }

            if (modalRect.top > window.innerHeight - headerHeight) {
                this.modalY = this.modalY - (modalRect.top - (window.innerHeight - headerHeight - 20));
            }
        },
        
        /**
         * Main validation trigger - validates all metrics in fullScorecardData
         */
        validateAllMetrics(fullScorecardData, workspaceFirstDayOfWeek, workspaceTimezone = null) {
            this.validationInProgress = true;
            this.timezone = workspaceTimezone;
            this.consolidatedReport = {
                failures: [],
                totalMetrics: 0,
                metricsWithFailures: 0,
                totalFailures: 0
            };
            
            const metrics = fullScorecardData?.metrics || {};
            const workspace = fullScorecardData?.scorecard?.workspace || {};
            const firstDayOfWeek = workspaceFirstDayOfWeek || workspace.first_day_of_week || 'sunday';
            
            Object.entries(metrics).forEach(([metricId, metric]) => {
                const result = this.validateMetricAggregation(metric, firstDayOfWeek);
                if (result.failures.length > 0) {
                    this.consolidatedReport.failures.push({
                        metricId: metric.id,
                        metricName: metric.name,
                        sourceId: metric.source_id,
                        summaryMethod: metric.summary_method,
                        failures: result.failures
                    });
                    this.consolidatedReport.metricsWithFailures++;
                    this.consolidatedReport.totalFailures += result.failures.length;
                }
            });
            
            this.consolidatedReport.totalMetrics = Object.keys(metrics).length;
            this.validationInProgress = false;
            
            return this.consolidatedReport;
        },
        
        /**
         * Validate a single metric's aggregation - returns only failures
         */
        validateMetricAggregation(metric, firstDayOfWeek) {
            const failures = [];

            // Manual metrics have per-interval values entered independently by the user, and
            // calculated metrics are evaluated per-period from component metrics — neither
            // follows weekly = aggregate(daily), so aggregation validation doesn't apply.
            if (metric.source_id === 'manual' || metric.source_id === 'calculated') {
                return { failures };
            }

            // Sources with is_metrics_aggregation_enabled = false fetch each frequency
            // independently from the upstream API (e.g. HelpScout's reports endpoint
            // returns a true reply-weighted weekly average that's NOT mean(daily values)).
            // Validating those against a derived aggregation produces guaranteed false
            // positives, so skip.
            if (metric.source && !Number(metric.source.is_metrics_aggregation_enabled)) {
                return { failures };
            }

            const summaryMethod = metric.summary_method || 'sum';
            const data = metric.data || {};
            
            // Phase 1: Validate daily -> weekly/monthly
            const dailyData = data.daily || {};
            if (Object.keys(dailyData).length > 0) {
                const latestDailyDate = this.getLatestDate(Object.keys(dailyData));
                
                // Validate weekly (last 4 weeks from daily)
                if (data.weekly && Object.keys(data.weekly).length > 0) {
                    const weeklyFailures = this.validatePeriodFromSource(
                        dailyData,
                        data.weekly,
                        'weekly',
                        summaryMethod,
                        firstDayOfWeek,
                        4,
                        latestDailyDate,
                        'daily'
                    );
                    failures.push(...weeklyFailures);
                }

                // Validate monthly (current month from daily)
                if (data.monthly && Object.keys(data.monthly).length > 0) {
                    const monthlyFailures = this.validatePeriodFromSource(
                        dailyData,
                        data.monthly,
                        'monthly',
                        summaryMethod,
                        firstDayOfWeek,
                        1,
                        latestDailyDate,
                        'daily'
                    );
                    failures.push(...monthlyFailures);
                }
            }
            
            // Phase 2: Validate monthly -> quarterly/yearly
            const monthlyData = data.monthly || {};
            if (Object.keys(monthlyData).length > 0) {
                const latestMonthDate = this.getLatestDate(Object.keys(monthlyData));
                
                // Validate quarterly (last 4 quarters from monthly)
                if (data.quarterly && Object.keys(data.quarterly).length > 0) {
                    const quarterlyFailures = this.validatePeriodFromSource(
                        monthlyData,
                        data.quarterly,
                        'quarterly',
                        summaryMethod,
                        firstDayOfWeek,
                        4,
                        latestMonthDate,
                        'monthly'
                    );
                    failures.push(...quarterlyFailures);
                }

                // Validate yearly (last year from monthly)
                if (data.yearly && Object.keys(data.yearly).length > 0) {
                    const yearlyFailures = this.validatePeriodFromSource(
                        monthlyData,
                        data.yearly,
                        'yearly',
                        summaryMethod,
                        firstDayOfWeek,
                        1,
                        latestMonthDate,
                        'monthly'
                    );
                    failures.push(...yearlyFailures);
                }
            }
            
            return { failures };
        },
        
        /**
         * Validate a specific period by comparing calculated values from source data
         * Returns only failures
         */
        validatePeriodFromSource(sourceData, targetData, period, summaryMethod, firstDayOfWeek, periodsToValidate, latestSourceDate, sourceFrequency) {
            const failures = [];

            // Group source data by target period
            const groupedSource = this.groupDataByPeriod(sourceData, period, firstDayOfWeek, sourceFrequency);

            // Get target periods to validate (most recent N periods).
            // Skip non-canonical target dates (e.g., a mid-month row inside data.monthly) so we
            // don't validate against a row that isn't a real period-start.
            const targetDates = Object.keys(targetData)
                .filter(date => date <= latestSourceDate)
                .filter(date => this.getPeriodKeyForDate(date, period, firstDayOfWeek) === date)
                .sort()
                .reverse()
                .slice(0, periodsToValidate);
            
            targetDates.forEach(targetDate => {
                if (!targetData[targetDate]) {
                    return;
                }
                const targetValue = targetData[targetDate].raw_value;

                const periodKey = this.getPeriodKeyForDate(targetDate, period, firstDayOfWeek);

                if (!groupedSource[periodKey]) {
                    return;
                }
                
                // Check if we should skip this period due to incomplete data
                if (!this.shouldValidatePeriod(periodKey, period, sourceData, firstDayOfWeek)) {
                    return;
                }
                
                const sourceItems = groupedSource[periodKey];
                const sourceValues = sourceItems.map(item => item.value);
                const calculatedValue = this.aggregateValues(sourceValues, summaryMethod);
                
                // Compare with tolerance
                const allowedDiff = Math.abs(calculatedValue) * this.TOLERANCE_PERCENT;
                const diff = Math.abs(targetValue - calculatedValue);
                const passed = diff <= allowedDiff;
                
                // Only record failures
                if (!passed) {
                    failures.push({
                        period: period.charAt(0).toUpperCase() + period.slice(1),
                        periodKey: periodKey,
                        date: targetDate,
                        calculated: calculatedValue,
                        actual: targetValue,
                        summaryMethod,
                        sourceCount: sourceValues.length,
                        sourceDetails: sourceItems,
                        difference: diff,
                        allowedDiff,
                        tolerancePercent: this.TOLERANCE_PERCENT
                    });
                }
            });
            
            return failures;
        },
        
        /**
         * Group source data by target period
         */
        groupDataByPeriod(sourceData, targetPeriod, firstDayOfWeek, sourceFrequency) {
            const grouped = {};

            Object.entries(sourceData).forEach(([date, dataPoint]) => {
                if (!dataPoint) return;

                // Skip non-canonical source dates so monthly buckets don't sum
                // mid-month snapshot rows alongside the real month-start values.
                if (sourceFrequency && this.getPeriodKeyForDate(date, sourceFrequency, firstDayOfWeek) !== date) {
                    return;
                }

                const periodKey = this.getPeriodKeyForDate(date, targetPeriod, firstDayOfWeek);
                if (!grouped[periodKey]) {
                    grouped[periodKey] = [];
                }

                grouped[periodKey].push({
                    date: date,
                    value: dataPoint.raw_value
                });
            });

            return grouped;
        },
        
        /**
         * Get period key for a date based on target period type
         */
        getPeriodKeyForDate(dateStr, period, firstDayOfWeek) {
            // Parse date parts to avoid timezone issues
            const parts = dateStr.split('-');
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const day = parseInt(parts[2], 10);
            
            if (period === 'weekly') {
                const weekStart = this.getWeekStart(year, month, day, firstDayOfWeek);
                return weekStart;
            } else if (period === 'monthly') {
                return `${year}-${String(month).padStart(2, '0')}-01`;
            } else if (period === 'quarterly') {
                const quarter = Math.floor((month - 1) / 3);
                const quarterStartMonth = quarter * 3 + 1;
                return `${year}-${String(quarterStartMonth).padStart(2, '0')}-01`;
            } else if (period === 'yearly') {
                return `${year}-01-01`;
            }
            
            return dateStr;
        },
        
        /**
         * Get the start of the week for a given date (avoiding timezone issues)
         */
        getWeekStart(year, month, day, firstDayOfWeek) {
            // Use UTC to avoid timezone issues
            const date = new Date(Date.UTC(year, month - 1, day));
            
            // Get day name
            const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const currentDayName = dayNames[date.getUTCDay()];
            
            // Calculate days to subtract
            const daysToSubtract = this.getDaysToWeekStart(currentDayName, firstDayOfWeek);
            
            // Subtract days to get week start
            const weekStart = new Date(date);
            weekStart.setUTCDate(date.getUTCDate() - daysToSubtract);
            
            return `${weekStart.getUTCFullYear()}-${String(weekStart.getUTCMonth() + 1).padStart(2, '0')}-${String(weekStart.getUTCDate()).padStart(2, '0')}`;
        },
        
        /**
         * Calculate days to subtract to get to week start
         */
        getDaysToWeekStart(currentDay, firstDayOfWeek) {
            const days = {
                'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
                'thursday': 4, 'friday': 5, 'saturday': 6
            };
            
            const currentDayNum = days[currentDay.toLowerCase()];
            const firstDayNum = days[firstDayOfWeek.toLowerCase()];
            
            const diff = currentDayNum - firstDayNum;
            return diff < 0 ? diff + 7 : diff;
        },
        
        /**
         * Aggregate values based on summary method
         */
        aggregateValues(values, summaryMethod) {
            if (!values || values.length === 0) return 0;
            
            if (summaryMethod === 'sum') {
                return values.reduce((sum, val) => sum + val, 0);
            } else if (summaryMethod === 'average') {
                const sum = values.reduce((sum, val) => sum + val, 0);
                return sum / values.length;
            } else if (summaryMethod === 'latest') {
                return values[values.length - 1];
            }
            
            return 0;
        },
        
        /**
         * Get the latest date from an array of date strings
         */
        getLatestDate(dates) {
            if (!dates || dates.length === 0) return null;
            return dates.sort().reverse()[0];
        },
        
        /**
         * Get all dates in a given period
         */
        getDatesInPeriod(periodKey, period, firstDayOfWeek) {
            const parts = periodKey.split('-');
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const day = parseInt(parts[2], 10);
            
            const dates = [];
            
            if (period === 'weekly') {
                // Get 7 days starting from the period key (which is the week start)
                for (let i = 0; i < 7; i++) {
                    const date = new Date(Date.UTC(year, month - 1, day + i));
                    dates.push(`${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`);
                }
            } else if (period === 'monthly') {
                // Get all days in the month
                const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
                for (let d = 1; d <= daysInMonth; d++) {
                    dates.push(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
                }
            }
            
            return dates;
        },
        
        /**
         * Check if we have complete data for a period
         * Returns true if we should validate this period, false if we should skip it
         */
        shouldValidatePeriod(periodKey, period, sourceData, firstDayOfWeek) {
            const today = workspaceToday(this.timezone);
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            
            if (period === 'weekly') {
                // Get all dates in this week
                const weekDates = this.getDatesInPeriod(periodKey, 'weekly', firstDayOfWeek);
                
                // Check if this is the current or future week
                const lastDayOfWeek = weekDates[weekDates.length - 1];
                if (lastDayOfWeek >= todayStr) {
                    // Current or future week - skip it
                    return false;
                }
                
                // Check if we have all daily data for this week
                const availableDates = Object.keys(sourceData);
                const missingDates = weekDates.filter(date => !availableDates.includes(date));
                
                if (missingDates.length > 0) {
                    // Missing some daily data for this week
                    return false;
                }
            } else if (period === 'monthly') {
                // Get year and month from period key
                const parts = periodKey.split('-');
                const year = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10);
                
                // Check if this is the current or future month
                const currentYear = today.getFullYear();
                const currentMonth = today.getMonth() + 1;
                
                if (year > currentYear || (year === currentYear && month >= currentMonth)) {
                    // Current or future month - skip it
                    return false;
                }

                const firstDayOfMonth = `${year}-${String(month).padStart(2, '0')}-01`;
                // month is 1-indexed; Date.UTC expects 0-indexed, so day=0 gives last day of target month
                const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
                const lastDayOfMonth = `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
                
                const sourceKeys = Object.keys(sourceData);
                const hasFirstDay = sourceKeys.includes(firstDayOfMonth);
                const hasLastDay = sourceKeys.includes(lastDayOfMonth);
                
                if (!hasFirstDay || !hasLastDay) {
                    return false;
                }
                
            } else if (period === 'quarterly') {
                // For quarterly, check boundary months (first and last) of the quarter
                const parts = periodKey.split('-');
                const year = parseInt(parts[0], 10);
                const quarterStartMonth = parseInt(parts[1], 10);
                
                // Get the first and last month of this quarter
                const firstMonthOfQuarter = `${year}-${String(quarterStartMonth).padStart(2, '0')}`;
                const lastMonthOfQuarter = `${year}-${String(quarterStartMonth + 2).padStart(2, '0')}`;
                
                // Check if any of these boundary months are current or future
                const currentYear = today.getFullYear();
                const currentMonth = today.getMonth() + 1;
                
                // Check first month
                const firstParts = firstMonthOfQuarter.split('-');
                const firstYear = parseInt(firstParts[0], 10);
                const firstMonth = parseInt(firstParts[1], 10);
                
                if (firstYear > currentYear || (firstYear === currentYear && firstMonth >= currentMonth)) {
                    return false;
                }
                
                // Check last month
                const lastParts = lastMonthOfQuarter.split('-');
                const lastYear = parseInt(lastParts[0], 10);
                const lastMonth = parseInt(lastParts[1], 10);
                
                if (lastYear > currentYear || (lastYear === currentYear && lastMonth >= currentMonth)) {
                    return false;
                }
                
                // Check if we have the boundary months (first and last)
                // Missing middle month is okay (could be null in DB)
                const sourceKeys = Object.keys(sourceData);
                const hasFirstMonth = sourceKeys.some(date => date.startsWith(firstMonthOfQuarter));
                const hasLastMonth = sourceKeys.some(date => date.startsWith(lastMonthOfQuarter));
                
                if (!hasFirstMonth || !hasLastMonth) {
                    // Missing boundary months - can't validate this quarter
                    return false;
                }
            } else if (period === 'yearly') {
                // Check if this is current or future year
                const parts = periodKey.split('-');
                const year = parseInt(parts[0], 10);
                const currentYear = today.getFullYear();
                
                if (year >= currentYear) {
                    // Current or future year - skip it
                    return false;
                }
                
                // Check if we have the boundary months (first and last) of this year
                // We need January (first month) and December (last month) to validate
                // Missing data in between is okay (could be null values in DB)
                const firstMonth = `${year}-01`; // January
                const lastMonth = `${year}-12`;  // December
                
                const hasFirstMonth = Object.keys(sourceData).some(date => date.startsWith(firstMonth));
                const hasLastMonth = Object.keys(sourceData).some(date => date.startsWith(lastMonth));
                
                if (!hasFirstMonth || !hasLastMonth) {
                    // Missing boundary months - can't validate this year
                    return false;
                }
            }
            
            return true;
        },
        
        /**
         * Check if there are any validation failures
         */
        hasFailures() {
            return this.consolidatedReport.totalFailures > 0;
        },
        
        /**
         * Open modal with consolidated validation report
         */
        openValidationModal() {
            this.modalOpen = true;
        },
        
        /**
         * Close validation modal
         */
        closeValidationModal() {
            this.modalOpen = false;
        },
        
        /**
         * Get summary statistics
         */
        getValidationSummary() {
            return {
                total: this.consolidatedReport.totalMetrics,
                metricsWithFailures: this.consolidatedReport.metricsWithFailures,
                totalFailures: this.consolidatedReport.totalFailures
            };
        }
    },
    
    // Initialization method
    initMetricValidation() {
        // Run validation on initialization
        this.$nextTick(() => {
            if (this.fullScorecardData) {
                this.metricValidator.validateAllMetrics(
                    this.fullScorecardData,
                    this.firstDayOfWeek,
                    this.workspaceTimezone
                );
            }
        });
    },
    
    // Cleanup method
    destroyMetricValidation() {
        this.metricValidator.consolidatedReport = {
            failures: [],
            totalMetrics: 0,
            metricsWithFailures: 0,
            totalFailures: 0
        };
        this.metricValidator.modalOpen = false;
    }
});
